(() => {
  'use strict';

  const STORAGE_KEY = 'roomtek_quote_list_v1';
  const MAX_QUANTITY = 9999;
  const DEFAULT_WHATSAPP_PHONE = '5512981259174';
  const state = {
    items: [],
    lastFocusedElement: null,
    closeTimer: null
  };

  const toText = (value, maxLength = 500) => String(value ?? '').trim().slice(0, maxLength);

  const toQuantity = (value) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return 1;
    return Math.min(MAX_QUANTITY, Math.max(1, parsed));
  };

  const safeProductUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '#';
      if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
      return url.href;
    } catch (_error) {
      return '#';
    }
  };

  const safeImageUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  const normalizeItem = (item) => {
    if (!item || typeof item !== 'object') return null;

    const id = toText(item.id, 80);
    const title = toText(item.title, 240);
    if (!id || !title) return null;

    return {
      id,
      productId: toText(item.productId, 80),
      title,
      variantTitle: toText(item.variantTitle, 240),
      sku: toText(item.sku, 160),
      url: safeProductUrl(item.url),
      image: safeImageUrl(item.image),
      options: Array.isArray(item.options) ? item.options.map((option) => toText(option, 160)).filter(Boolean) : [],
      quantity: toQuantity(item.quantity),
      addedAt: toText(item.addedAt, 40)
    };
  };

  const readItems = () => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeItem).filter(Boolean);
    } catch (_error) {
      return [];
    }
  };

  const itemCount = () => state.items.reduce((total, item) => total + item.quantity, 0);

  const setFormStatus = (element, message, status = '') => {
    if (!element) return;
    element.textContent = message;
    if (status) element.dataset.state = status;
    else element.removeAttribute('data-state');
  };

  const announceStorageError = () => {
    document.querySelectorAll('[data-quote-add-status], [data-quote-form-status]').forEach((element) => {
      element.textContent = 'Não foi possível salvar a lista neste navegador. Verifique as permissões de armazenamento.';
      if (element.matches('[data-quote-form-status]')) element.dataset.state = 'error';
    });
  };

  const captureListFocus = () => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return null;

    const root = active.closest('[data-quote-list]');
    if (!root) return null;

    const item = active.closest('.quote-list__item');
    const itemElements = Array.from(root.querySelectorAll('.quote-list__item'));
    return {
      root,
      itemId: toText(active.dataset.quoteItemId || item?.dataset.quoteItemId, 80),
      action: active.dataset.quoteAction || (active.matches('[data-quote-quantity-input]') ? 'quantity-input' : ''),
      itemIndex: item ? itemElements.indexOf(item) : -1
    };
  };

  const restoreListFocus = (focusState) => {
    if (!focusState) return;
    const root = focusState.root;
    if (!(root instanceof HTMLElement) || !root.isConnected) return;

    const drawer = root.closest('[data-quote-drawer]');
    if (drawer?.hidden) return;

    const item = Array.from(root.querySelectorAll('.quote-list__item'))
      .find((element) => element.dataset.quoteItemId === focusState.itemId);
    let target = null;

    if (item && focusState.action === 'quantity-input') {
      target = item.querySelector('[data-quote-quantity-input]');
    } else if (item && focusState.action) {
      target = Array.from(item.querySelectorAll('[data-quote-action]'))
        .find((element) => element.dataset.quoteAction === focusState.action);
    }

    if (!target) {
      const remainingItems = Array.from(root.querySelectorAll('.quote-list__item'));
      const fallbackItem = remainingItems[Math.min(Math.max(focusState.itemIndex, 0), remainingItems.length - 1)];
      target = fallbackItem?.querySelector('.quote-list__title, [data-quote-action]')
        || root.querySelector('.quote-list__empty-action')
        || root.querySelector('.quote-page__continue-link')
        || drawer?.querySelector('.quote-drawer__review')
        || drawer?.querySelector('.quote-drawer__close');
    }

    if (target instanceof HTMLButtonElement && target.disabled) {
      target = item?.querySelector('[data-quote-quantity-input], [data-quote-action="increase"]') || target;
    }

    if (target instanceof HTMLElement) {
      window.requestAnimationFrame(() => {
        if (target.isConnected && !target.matches(':disabled')) {
          target.focus({ preventScroll: true });
        }
      });
    }
  };

  const saveItems = (items) => {
    const normalized = items.map(normalizeItem).filter(Boolean);
    const listFocus = captureListFocus();

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (_error) {
      announceStorageError();
      return false;
    }

    state.items = normalized;
    renderAll();
    restoreListFocus(listFocus);
    document.dispatchEvent(new CustomEvent('roomtek:quote-updated', {
      detail: { items: state.items.slice(), count: itemCount() }
    }));
    return true;
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const buildItemElement = (item) => {
    const article = createElement('article', 'quote-list__item');
    article.dataset.quoteItemId = item.id;

    const imageLink = createElement('a', 'quote-list__image-link');
    imageLink.href = item.url;
    imageLink.setAttribute('aria-label', `Ver ${item.title}`);

    if (item.image) {
      const image = createElement('img', 'quote-list__image');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 160;
      image.height = 160;
      imageLink.append(image);
    } else {
      imageLink.append(createElement('span', 'quote-list__image-placeholder', 'Sem imagem'));
    }

    const details = createElement('div', 'quote-list__details');
    const titleRow = createElement('div', 'quote-list__title-row');
    const title = createElement('a', 'quote-list__title', item.title);
    title.href = item.url;

    const removeButton = createElement('button', 'quote-list__remove', 'Remover');
    removeButton.type = 'button';
    removeButton.dataset.quoteAction = 'remove';
    removeButton.dataset.quoteItemId = item.id;
    removeButton.setAttribute('aria-label', `Remover ${item.title} da lista`);
    titleRow.append(title, removeButton);

    const meta = createElement('div', 'quote-list__meta');
    if (item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') {
      meta.append(createElement('span', '', `Modelo: ${item.variantTitle}`));
    }
    if (item.sku) meta.append(createElement('span', '', `Código: ${item.sku}`));

    const quantity = createElement('div', 'quote-list__quantity');
    quantity.append(createElement('span', '', 'Quantidade'));

    const controls = createElement('div', 'quote-list__quantity-controls');
    const decrease = createElement('button', 'quote-list__quantity-button', '−');
    decrease.type = 'button';
    decrease.dataset.quoteAction = 'decrease';
    decrease.dataset.quoteItemId = item.id;
    decrease.disabled = item.quantity <= 1;
    decrease.setAttribute('aria-disabled', String(item.quantity <= 1));
    decrease.setAttribute('aria-label', `Diminuir quantidade de ${item.title}`);

    const input = createElement('input', 'quote-list__quantity-input');
    input.type = 'number';
    input.min = '1';
    input.max = String(MAX_QUANTITY);
    input.step = '1';
    input.inputMode = 'numeric';
    input.value = String(item.quantity);
    input.dataset.quoteQuantityInput = '';
    input.dataset.quoteItemId = item.id;
    input.setAttribute('aria-label', `Quantidade de ${item.title}`);

    const increase = createElement('button', 'quote-list__quantity-button', '+');
    increase.type = 'button';
    increase.dataset.quoteAction = 'increase';
    increase.dataset.quoteItemId = item.id;
    increase.setAttribute('aria-label', `Aumentar quantidade de ${item.title}`);

    controls.append(decrease, input, increase);
    quantity.append(controls);
    details.append(titleRow, meta, quantity);
    article.append(imageLink, details);
    return article;
  };

  const renderListRoot = (root) => {
    const itemsContainer = root.querySelector('[data-quote-items]');
    const empty = root.querySelector('[data-quote-empty]');
    const populated = root.querySelector('[data-quote-populated]');
    if (!itemsContainer || !empty || !populated) return;

    const fragment = document.createDocumentFragment();
    state.items.forEach((item) => fragment.append(buildItemElement(item)));
    itemsContainer.replaceChildren(fragment);

    const hasItems = state.items.length > 0;
    empty.hidden = hasItems;
    populated.hidden = !hasItems;
  };

  const updateCountLabels = () => {
    const count = itemCount();
    document.querySelectorAll('[data-quote-count]').forEach((element) => {
      element.textContent = String(count);
    });
    document.querySelectorAll('[data-quote-count-label]').forEach((element) => {
      element.textContent = count === 1 ? 'item selecionado' : 'itens selecionados';
    });
  };

  const updateHeaderCounter = () => {
    const count = itemCount();
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('aria-label', count === 1 ? 'Abrir lista de orçamento com 1 item' : `Abrir lista de orçamento com ${count} itens`);
    const hiddenLabel = cartLink.querySelector('.visually-hidden');
    if (hiddenLabel) hiddenLabel.textContent = 'Lista de orçamento';

    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (count === 0) {
      if (bubble) bubble.remove();
      return;
    }

    if (!bubble) {
      bubble = createElement('div', 'cart-count-bubble');
      cartLink.append(bubble);
    }

    const visibleCount = createElement('span', '', count > 99 ? '99+' : String(count));
    visibleCount.setAttribute('aria-hidden', 'true');
    const accessibleCount = createElement('span', 'visually-hidden', count === 1 ? '1 item na lista de orçamento' : `${count} itens na lista de orçamento`);
    bubble.replaceChildren(visibleCount, accessibleCount);
  };

  const updateFormState = () => {
    const disabled = state.items.length === 0;
    const count = itemCount();
    document.querySelectorAll('[data-quote-whatsapp], [data-quote-drawer-whatsapp]').forEach((button) => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });

    document.querySelectorAll('[data-quote-drawer-whatsapp]').forEach((button) => {
      button.setAttribute(
        'aria-label',
        disabled
          ? 'Adicione itens para enviar o orçamento pelo WhatsApp'
          : `Enviar orçamento com ${count} ${count === 1 ? 'item' : 'itens'} pelo WhatsApp`
      );
    });

    document.querySelectorAll('[data-quote-form-readiness]').forEach((element) => {
      element.dataset.state = disabled ? 'pending' : 'ready';
      const message = element.querySelector('[data-quote-form-readiness-text]');
      if (!message) return;
      message.textContent = disabled
        ? 'Adicione ao menos um produto para liberar o envio.'
        : count === 1
          ? '1 item pronto para ser enviado ao atendimento.'
          : `${count} itens prontos para serem enviados ao atendimento.`;
    });
  };

  const setDrawerStatus = (message, status = '') => {
    const container = document.querySelector('[data-quote-drawer-status]');
    const text = container?.querySelector('[data-quote-drawer-status-text]');
    if (!container || !text) return;

    text.textContent = message;
    if (status) container.dataset.state = status;
    else container.removeAttribute('data-state');
  };

  function renderAll() {
    document.querySelectorAll('[data-quote-list]').forEach(renderListRoot);
    updateCountLabels();
    updateHeaderCounter();
    updateFormState();

    const drawerFooter = document.querySelector('[data-quote-drawer-footer]');
    if (drawerFooter) drawerFooter.hidden = state.items.length === 0;
    setDrawerStatus('Os itens e quantidades serão enviados diretamente ao atendimento.');
  }

  const openDrawer = (trigger) => {
    const drawer = document.querySelector('[data-quote-drawer]');
    if (!drawer) return;

    window.clearTimeout(state.closeTimer);
    state.lastFocusedElement = trigger instanceof HTMLElement ? trigger : document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quote-drawer-open');

    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      const closeButton = drawer.querySelector('.quote-drawer__close');
      if (closeButton) closeButton.focus({ preventScroll: true });
    });
  };

  const closeDrawer = () => {
    const drawer = document.querySelector('[data-quote-drawer]');
    if (!drawer || drawer.hidden) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quote-drawer-open');
    state.closeTimer = window.setTimeout(() => {
      drawer.hidden = true;
    }, 230);

    if (state.lastFocusedElement instanceof HTMLElement) {
      state.lastFocusedElement.focus({ preventScroll: true });
    }
  };

  const getVariants = (container) => {
    const source = container.querySelector('[data-quote-variants]');
    if (!source) return [];
    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (_error) {
      return [];
    }
  };

  const selectedItemFromButton = (button) => {
    const container = button.closest('[data-quote-product]');
    if (!container) return null;

    const variantInput = container.querySelector('.product-variant-id, input[name="id"]');
    const variantId = toText(variantInput?.value, 80);
    const variants = getVariants(container);
    const variant = variants.find((entry) => String(entry.id) === variantId) || variants[0] || {};
    const selectedId = toText(variant.id || variantId, 80);
    if (!selectedId) return null;

    const quantityInput = container.querySelector('[data-quote-product-quantity]');
    const quantity = toQuantity(quantityInput?.value || 1);
    if (quantityInput) quantityInput.value = String(quantity);

    const productUrl = safeProductUrl(container.dataset.productUrl || '#');
    let variantUrl = productUrl;
    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', selectedId);
      variantUrl = url.origin === window.location.origin ? `${url.pathname}${url.search}` : url.href;
    } catch (_error) {
      variantUrl = productUrl;
    }

    const featuredImage = variant.featured_image && typeof variant.featured_image === 'object'
      ? variant.featured_image.src
      : variant.featured_image;

    return normalizeItem({
      id: selectedId,
      productId: container.dataset.productId,
      title: container.dataset.productTitle,
      variantTitle: variant.public_title || variant.title || '',
      sku: variant.sku || '',
      url: variantUrl,
      image: featuredImage || container.dataset.productImage || '',
      options: variant.options || [],
      quantity,
      addedAt: new Date().toISOString()
    });
  };

  const addFromButton = (button) => {
    const item = selectedItemFromButton(button);
    const status = button.closest('[data-quote-product]')?.querySelector('[data-quote-add-status]');
    if (!item) {
      if (status) status.textContent = 'Selecione um modelo válido antes de adicionar.';
      return;
    }

    const existing = state.items.find((entry) => entry.id === item.id);
    const nextItems = existing
      ? state.items.map((entry) => entry.id === item.id
        ? { ...entry, ...item, quantity: toQuantity(entry.quantity + item.quantity) }
        : entry)
      : [...state.items, item];

    if (!saveItems(nextItems)) return;
    if (status) status.textContent = `${item.title} foi adicionado à lista.`;
    openDrawer(button);
  };

  const updateItemQuantity = (id, quantity) => {
    saveItems(state.items.map((item) => item.id === id ? { ...item, quantity: toQuantity(quantity) } : item));
  };

  const handleListAction = (button) => {
    const id = toText(button.dataset.quoteItemId, 80);
    const item = state.items.find((entry) => entry.id === id);
    if (!item) return;

    if (button.dataset.quoteAction === 'remove') {
      saveItems(state.items.filter((entry) => entry.id !== id));
      return;
    }

    const delta = button.dataset.quoteAction === 'increase' ? 1 : -1;
    updateItemQuantity(id, item.quantity + delta);
  };

  const isPath = (href, expectedPath) => {
    try {
      const url = new URL(href, window.location.origin);
      const actual = url.pathname.replace(/\/+$/, '') || '/';
      const expected = expectedPath.replace(/\/+$/, '') || '/';
      return url.origin === window.location.origin && actual === expected;
    } catch (_error) {
      return false;
    }
  };

  const isCheckoutLink = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      return url.origin === window.location.origin && /^\/checkout(?:\/|$)/.test(url.pathname);
    } catch (_error) {
      return false;
    }
  };

  const formatCnpj = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const absoluteProductUrl = (value) => {
    try {
      return new URL(value, window.location.origin).href;
    } catch (_error) {
      return window.location.origin;
    }
  };

  const appendWhatsAppItems = (lines) => {
    state.items.forEach((item, index) => {
      const meaningfulOptions = item.options.filter((option) => option.toLowerCase() !== 'default title');
      lines.push(`${index + 1}. ${item.title}`);
      if (item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') {
        lines.push(`Modelo: ${item.variantTitle}`);
      } else if (meaningfulOptions.length) {
        lines.push(`Opções: ${meaningfulOptions.join(' | ')}`);
      }
      if (item.sku) lines.push(`Código: ${item.sku}`);
      lines.push(`Quantidade: ${item.quantity}`);
      lines.push(`Link: ${absoluteProductUrl(item.url)}`);
      if (index < state.items.length - 1) lines.push('');
    });
  };

  const buildWhatsAppMessage = (form) => {
    const data = new FormData(form);
    const lines = [
      'Olá! Gostaria de solicitar um orçamento.',
      '',
      'DADOS PARA ATENDIMENTO',
      `Nome: ${toText(data.get('name'), 100)}`,
      `Empresa: ${toText(data.get('company'), 120)}`,
      `CNPJ: ${toText(data.get('cnpj'), 20)}`,
      `Cidade/UF: ${toText(data.get('city'), 100)} / ${toText(data.get('state'), 2).toUpperCase()}`,
      '',
      'ITENS SOLICITADOS'
    ];

    appendWhatsAppItems(lines);

    const notes = toText(data.get('notes'), 800);
    if (notes) lines.push('', 'OBSERVAÇÕES', notes);
    return lines.join('\n');
  };

  const buildDrawerWhatsAppMessage = () => {
    const lines = [
      'Olá! Gostaria de solicitar um orçamento.',
      '',
      'ITENS SOLICITADOS'
    ];
    appendWhatsAppItems(lines);
    return lines.join('\n');
  };

  const openWhatsApp = (phoneValue, message) => {
    const phone = toText(phoneValue || DEFAULT_WHATSAPP_PHONE, 20).replace(/\D/g, '');
    if (!phone) return false;

    const link = document.createElement('a');
    link.href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    return true;
  };

  const submitWhatsApp = (form) => {
    const status = form.querySelector('[data-quote-form-status]');
    setFormStatus(status, '');
    const cnpjInput = form.elements.cnpj;
    const cnpjDigits = String(cnpjInput?.value || '').replace(/\D/g, '');
    if (cnpjInput) {
      cnpjInput.setCustomValidity(cnpjDigits.length === 14 ? '' : 'Informe um CNPJ com 14 dígitos.');
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      setFormStatus(status, 'Revise os campos obrigatórios antes de continuar.', 'error');
      return;
    }

    if (state.items.length === 0) {
      setFormStatus(status, 'Adicione pelo menos um produto à lista.', 'error');
      return;
    }

    const message = buildWhatsAppMessage(form);
    const opened = openWhatsApp(form.dataset.whatsappPhone, message);

    if (!opened) {
      setFormStatus(status, 'O número de WhatsApp para atendimento não está configurado.', 'error');
      return;
    }

    setFormStatus(status, 'O WhatsApp foi aberto com sua solicitação. A lista foi mantida neste navegador.', 'success');
  };

  const submitDrawerWhatsApp = (button) => {
    if (state.items.length === 0) {
      setDrawerStatus('Adicione pelo menos um produto antes de enviar.', 'error');
      return;
    }

    const opened = openWhatsApp(button.dataset.whatsappPhone, buildDrawerWhatsAppMessage());
    setDrawerStatus(
      opened
        ? 'O WhatsApp foi aberto com os itens da sua lista.'
        : 'O número de WhatsApp para atendimento não está configurado.',
      opened ? 'success' : 'error'
    );
  };

  const handleDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest('[data-quote-add]');
    if (addButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      addFromButton(addButton);
      return;
    }

    const drawerWhatsAppButton = target.closest('[data-quote-drawer-whatsapp]');
    if (drawerWhatsAppButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitDrawerWhatsApp(drawerWhatsAppButton);
      return;
    }

    const productQuantityButton = target.closest('[data-quote-product-quantity-change]');
    if (productQuantityButton) {
      event.preventDefault();
      const container = productQuantityButton.closest('[data-quote-product]');
      const input = container?.querySelector('[data-quote-product-quantity]');
      if (input) {
        const delta = Number.parseInt(productQuantityButton.dataset.quoteProductQuantityChange, 10) || 0;
        input.value = String(toQuantity(toQuantity(input.value) + delta));
      }
      return;
    }

    const listAction = target.closest('[data-quote-action]');
    if (listAction) {
      event.preventDefault();
      handleListAction(listAction);
      return;
    }

    const closeButton = target.closest('[data-quote-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const link = target.closest('a[href]');
    if (!link || link.hasAttribute('data-quote-page-link')) return;

    const cartPath = window.routes?.cart_url || '/cart';
    if (isPath(link.href, cartPath) || isCheckoutLink(link.href)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openDrawer(link);
    }
  };

  const handleDocumentSubmit = (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;

    if (form.matches('[data-quote-form]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitWhatsApp(form);
      return;
    }

    let actionPath = '';
    try {
      actionPath = new URL(form.action || window.location.href, window.location.origin).pathname;
    } catch (_error) {
      actionPath = '';
    }

    const submitter = event.submitter;
    const isAddToCart = /\/cart\/add(?:\.js)?\/?$/.test(actionPath);
    const isCheckout = submitter?.name === 'checkout' || /\/checkout(?:\/|$)/.test(actionPath);
    if (isAddToCart || isCheckout) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openDrawer(submitter || form);
    }
  };

  const handleInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.matches('[data-quote-quantity-input]')) {
      const id = toText(input.dataset.quoteItemId, 80);
      updateItemQuantity(id, input.value);
      return;
    }

    if (input.name === 'cnpj' && input.closest('[data-quote-form]')) {
      input.value = formatCnpj(input.value);
      input.setCustomValidity('');
    }

    if (input.name === 'state' && input.closest('[data-quote-form]')) {
      input.value = input.value.replace(/[^a-z]/gi, '').slice(0, 2).toUpperCase();
    }

    if (input.matches('[data-quote-product-quantity]')) {
      input.value = String(toQuantity(input.value));
    }
  };

  const trapDrawerFocus = (event) => {
    const drawer = document.querySelector('[data-quote-drawer]');
    if (!drawer || drawer.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = Array.from(drawer.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const init = () => {
    document.documentElement.classList.add('catalog-mode');
    state.items = readItems();
    renderAll();

    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('submit', handleDocumentSubmit, true);
    document.addEventListener('change', handleInput);
    document.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.name === 'cnpj' || input.name === 'state') handleInput(event);
    });
    document.addEventListener('keydown', trapDrawerFocus);
    document.addEventListener('shopify:section:load', renderAll);
    window.addEventListener('pageshow', renderAll);
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      state.items = readItems();
      renderAll();
    });

    window.RoomtekQuoteList = Object.freeze({
      open: openDrawer,
      close: closeDrawer,
      getItems: () => state.items.map((item) => ({ ...item }))
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
